import { Injectable } from '@angular/core';
import { merge, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, take, throttleTime, timeout } from 'rxjs/operators';

import { Files } from '../interfaces/claims/files.interface';
import { hasStaleSasUrl } from '../utils/file.util';

const ERROR_REFRESH_THROTTLE_MS = 60000;
const REQUEST_REFRESH_THROTTLE_MS = 10000;
const FRESH_FILE_TIMEOUT_MS = 10000;

@Injectable({
  providedIn: 'root',
})
export class ThumbnailRefreshService {
  /**
   * Emits when file lists holding SAS URLs should be re-fetched — either a
   * rendered thumbnail failed to load, or a user opened a file whose SAS
   * token already expired. Stores that own file lists subscribe and re-fetch
   * so bindings receive fresh URLs. Both triggers are throttled so bursts
   * collapse into a single refresh.
   */
  readonly refresh$: Observable<void>;

  private readonly _thumbnailFailed$ = new Subject<void>();
  private readonly _refreshRequested$ = new Subject<void>();

  constructor() {
    this.refresh$ = merge(
      this._thumbnailFailed$.pipe(throttleTime(ERROR_REFRESH_THROTTLE_MS)),
      this._refreshRequested$.pipe(throttleTime(REQUEST_REFRESH_THROTTLE_MS)),
    );
  }

  notifyThumbnailFailed(failedSrc: string): void {
    const isRemoteUrl = /^https?:\/\//i.test(failedSrc) && !failedSrc.startsWith(window.location.origin);
    if (isRemoteUrl) {
      this._thumbnailFailed$.next();
    }
  }

  requestRefresh(): void {
    this._refreshRequested$.next();
  }

  /**
   * Returns the file to open. While its SAS URLs are still valid the file is
   * returned as is; otherwise a list refresh is requested and the matching
   * file from the re-fetched list is returned. Falls back to the original
   * file if no fresh version arrives in time.
   */
  ensureFreshFile(file: Files, filesList$: Observable<Files[]>): Observable<Files> {
    if (!hasStaleSasUrl(file)) {
      return of(file);
    }
    this.requestRefresh();
    return filesList$.pipe(
      map(files => files.find(freshFile => freshFile.id === file.id)),
      filter((freshFile): freshFile is Files => !!freshFile && !hasStaleSasUrl(freshFile)),
      take(1),
      timeout(FRESH_FILE_TIMEOUT_MS),
      catchError(() => of(file)),
    );
  }
}
